import { Outlet } from 'react-router'
import { Loader2, ShieldX } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Sidebar } from './Sidebar'
import { MobileNav } from './MobileNav'
import { LoginPage } from './LoginPage'
import { useAuth } from '@/lib/firebase/auth'
import { useMe } from '@/hooks/queries/useAdmin'
import { VoypApiError } from '@/lib/api/client'

/**
 * Root layout and the admin gate.
 *
 * Like voyp-webapp's AppShell this renders sign-in *in place* rather than
 * redirecting, so a deep link (e.g. /calls/CA123) survives the sign-in round
 * trip. Being signed in is not enough: the server decides who is an admin, so
 * we probe GET /api/admin/me and honour its answer. 403 means "signed in but
 * not an admin" and is final; anything else is treated as a transient error.
 */
export function AdminShell() {
  const { status, user, signOutAdmin } = useAuth()
  const me = useMe()

  if (status === 'loading') return <FullPageSpinner />
  if (status === 'signedOut') return <LoginPage />
  if (me.isPending) return <FullPageSpinner />

  if (me.isError) {
    const forbidden = me.error instanceof VoypApiError && me.error.isForbidden
    return (
      <div className="flex min-h-full flex-col items-center justify-center gap-4 p-6 text-center">
        <ShieldX className="size-10 text-destructive" />
        <div className="space-y-1">
          <h1 className="font-display text-lg font-semibold">
            {forbidden ? 'Not authorized' : 'Could not verify access'}
          </h1>
          <p className="max-w-sm text-sm text-muted-foreground">
            {forbidden
              ? `${user?.email ?? 'This account'} does not have the admin role. Ask an existing admin to grant it.`
              : me.error.message}
          </p>
        </div>
        <div className="flex gap-2">
          {!forbidden && (
            <Button variant="outline" onClick={() => void me.refetch()}>
              Try again
            </Button>
          )}
          <Button variant="ghost" onClick={() => void signOutAdmin()}>
            Sign out
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="flex h-full">
      <Sidebar email={me.data.email} role={me.data.role} />
      <div className="flex min-w-0 flex-1 flex-col">
        <MobileNav email={me.data.email} role={me.data.role} />
        <main className="min-w-0 flex-1 overflow-y-auto">
          <div className="mx-auto max-w-[100rem] p-4 md:p-6">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  )
}

function FullPageSpinner() {
  return (
    <div className="flex min-h-full items-center justify-center">
      <Loader2 className="size-6 animate-spin text-muted-foreground" />
    </div>
  )
}
